import * as React from 'react';

import classnames from 'classnames';

import type {FilterObject} from 'nutshell-core/types';

import {addOffline} from 'shells/hoc/add-offline';
import {Radio} from 'shells/radio/radio';
import {RadioGroupSlider} from 'shells/radio/radio-group-slider';

import {addChartDataFetching} from '../charts/add-chart-data-fetching';
import {CardChart} from '../charts/card-chart';
import {ODRHeroChart} from '../charts/hero/odr-hero-chart';
import {makeChartClickable} from '../charts/make-chart-clickable';
import {getAxisConfig, getTickLabelFontStyles} from '../report-styles';
import type {
    ReportSegment,
    ODRReportData,
    ReportFacet,
    ReportGap,
    DateRangeType,
} from '../report-types';

import {FullScreenChartWrapper} from './fullscreen-chart-wrapper';
import {HeroChartHeader} from './hero-chart-header';

import cardWrapperStyles from './card-wrapper.module.css';
import styles from './on-demand-reports.module.css';

const ReportCardChart = addOffline(addChartDataFetching(CardChart));
const ClickablePivotChart = addOffline(addChartDataFetching(makeChartClickable(CardChart)));
const HeroChart = addOffline(addChartDataFetching(ODRHeroChart));

type ODRCard = {
    title: string;
    type: ReportSegment;
};

const getCardConfig = () => {
    return [
        {title: 'Assignee', type: 'assignees'},
        {title: 'Stage', type: 'stages'},
        {title: 'Product', type: 'products'},
        {title: 'Outcome', type: 'outcomes'},
        {title: 'Source', type: 'sources'},
        {title: 'Tag', type: 'tags'},
        {title: 'Competitor', type: 'competitors'},
        {title: 'Territory', type: 'territories'},
        {title: 'Age', type: 'age'},
        {title: 'Value', type: 'value'},
        {title: 'Channel', type: 'channels'},
    ].filter(
        (item) => item !== null && (NutClientConfig.hasTerritories || item.type !== 'territories')
    ); // Null entries may be from feature flag ternarys
};

const CARD_CHART_MAX_ROWS = 9;
const FULL_SCREEN_PIVOT_CHART_HASH_PREFIX = '#fullScreenPivotChart=';
const POSITION_ANIMATION_NAME = 'animate';

type Props = {
    height: number;
    width: number;
    isScrollContainer: boolean;
    filters: FilterObject[];
    dateRangeType: DateRangeType;
    reportGap: ReportGap;
    reportPivotFacet: ReportFacet;
    onUpdateReportParams: (newParams: any) => void;
};

type State = {
    isStickyHeaderActive: boolean;
    shouldCloseFullScreenPivotChart: boolean;
    fullScreenPivotData: ODRReportData | null | undefined;
    fullScreenPivotChart: ODRCard | null | undefined;
    fullScreenOnMount: boolean;
};

export class OnDemandReports extends React.Component<Props, State> {
    finalCard: HTMLElement | null | undefined;
    containerRef: HTMLElement | null | undefined;
    stickyWrapperRef: HTMLElement | null | undefined;
    previousHash: string | null | undefined;

    constructor(props: Props) {
        super(props);

        const defaultState = {
            shouldCloseFullScreenPivotChart: false,
            fullScreenPivotData: undefined,
            fullScreenPivotChart: undefined,
            fullScreenOnMount: false,
            isStickyHeaderActive: false,
        } as const;

        if (location.hash.startsWith(FULL_SCREEN_PIVOT_CHART_HASH_PREFIX)) {
            const pivotChartType = location.hash.replace(FULL_SCREEN_PIVOT_CHART_HASH_PREFIX, '');
            const pivotChart = getCardConfig().find((card) => card.type === pivotChartType);

            if (pivotChart) {
                // @ts-expect-error
                defaultState.fullScreenPivotChart = pivotChart;
                // @ts-expect-error
                defaultState.fullScreenOnMount = true;
            } else {
                history.replaceState({}, '', location.pathname + location.search);
            }
        }

        this.state = defaultState;
    }

    componentDidMount() {
        // Add a listener so we can fire a global event when all position animations end
        window.dispatchEvent(new CustomEvent('positionAnimationsStarted'));
        const cards = document.querySelectorAll(`.${cardWrapperStyles.card}`);
        // @ts-expect-error
        this.finalCard = cards[cards.length - 1];
        // @ts-expect-error
        this.finalCard.addEventListener('animationend', this.handleAnimationEnd);
    }

    componentWillUnmount() {
        if (this.finalCard)
            this.finalCard.removeEventListener('animationend', this.handleAnimationEnd);
    }

    render() {
        const {isScrollContainer} = this.props;

        return (
            <div className={classnames(styles.wrapper, styles.animate)}>
                <div
                    style={{
                        width: this.props.width,
                        height: isScrollContainer ? this.props.height : 'auto',
                        overflow: isScrollContainer ? 'auto' : 'visible',
                    }}
                    ref={(node) => {
                        this.containerRef = node;
                    }}
                    onScroll={this.handleScroll}
                    className={styles.container}
                >
                    {this.renderHero()}

                    {this.renderStickyHeader()}

                    {/* @ts-expect-error */}
                    {getCardConfig().map((card) => this.renderCard(card))}
                </div>
                {this.renderFullScreenPivotChart()}
            </div>
        );
    }

    renderHero = () => {
        return (
            <div id='chart-hero' className={styles['hero-container']}>
                <HeroChartHeader
                    dateRangeType={this.props.dateRangeType}
                    reportGap={this.props.reportGap}
                    onTypeChange={this.handleDateRangeTypeChange}
                    onGapChange={this.handleGapChange}
                />
                <HeroChart
                    isTimeseries={true}
                    dateRangeType={this.props.dateRangeType}
                    reportParams={{
                        gap: this.props.reportGap,
                    }}
                    filters={this.props.filters}
                />
            </div>
        );
    };

    renderStickyHeader = () => {
        const cardsHeaderStyleName =
            this.state.isStickyHeaderActive && this.props.isScrollContainer
                ? styles['sticky-header']
                : styles.header;

        return (
            <div
                ref={(node) => {
                    this.stickyWrapperRef = node;
                }}
                className={styles['sticky-header-wrapper']}
            >
                <div className={cardsHeaderStyleName}>{this.renderReportPivotFacetControl()}</div>
            </div>
        );
    };

    renderReportPivotFacetControl = () => {
        return (
            <RadioGroupSlider
                name='displayType'
                size='big'
                selectedValue={this.props.reportPivotFacet}
                selectedIndex={this.props.reportPivotFacet === 'count' ? 0 : 1}
                // @ts-expect-error
                onChange={this.handlePivotFacetChange}
            >
                <Radio value='count'>Lead quantity</Radio>
                <Radio value='total'>Lead value</Radio>
            </RadioGroupSlider>
        );
    };

    renderCard = (card: ODRCard) => {
        // @ts-expect-error
        const data = this.state[`${card.type}Data`];
        const isEmpty = Boolean(data && data.length === 0);

        const wrapperStyle = isEmpty ? cardWrapperStyles['card--empty'] : cardWrapperStyles.card;

        return (
            <div key={card.title} className={cardWrapperStyles.container}>
                <div className={wrapperStyle}>
                    <ClickablePivotChart
                        title={card.title}
                        reportParams={{
                            facet: this.props.reportPivotFacet,
                            segment: card.type,
                        }}
                        filters={this.props.filters}
                        axisConfig={getAxisConfig()}
                        tickLabelFontStyles={getTickLabelFontStyles()}
                        maxRows={CARD_CHART_MAX_ROWS}
                        isFullScreen={false}
                        onClickChart={this.handleShowFullScreenPivotChart}
                    />
                </div>
            </div>
        );
    };

    renderFullScreenPivotChart = () => {
        const card = this.state.fullScreenPivotChart;
        if (!card) return null;

        const chartData =
            this.state.fullScreenPivotData && this.state.fullScreenPivotData.chartData;
        let chartHeight = 0;

        if (chartData) {
            const verticalDomainPadding =
                CardChart.domainPadding.top + CardChart.domainPadding.bottom;
            chartHeight = chartData.length * CardChart.rowHeight + verticalDomainPadding;
        }

        const cardHeight = parseInt(cardWrapperStyles.height, 10);

        return (
            <FullScreenChartWrapper
                pivotFacetControlRenderer={this.renderReportPivotFacetControl}
                chartHeight={chartHeight >= cardHeight ? chartHeight : cardHeight}
                shouldClose={this.state.shouldCloseFullScreenPivotChart}
                onClose={this.handleCloseFullScreenPivotChart}
            >
                <ReportCardChart
                    title={card.title}
                    reportParams={{
                        facet: this.props.reportPivotFacet,
                        segment: card.type,
                    }}
                    filters={this.props.filters}
                    axisConfig={getAxisConfig(true)}
                    tickLabelFontStyles={getTickLabelFontStyles(true)}
                    // These are overrides
                    cachedData={this.state.fullScreenPivotData}
                    isFullScreen={true}
                />
            </FullScreenChartWrapper>
        );
    };

    handleShowFullScreenPivotChart = (cardType: ReportSegment, cardData: ODRReportData) => {
        const card = getCardConfig().find((c) => c.type === cardType);
        window.addEventListener('popstate', this.handleBackButtonFullScreenPivotChart);
        this.setState({
            // @ts-expect-error
            fullScreenPivotChart: card,
            fullScreenPivotData: cardData,
        });
        this.previousHash = location.hash;
        history.pushState({}, '', FULL_SCREEN_PIVOT_CHART_HASH_PREFIX + cardType);
    };

    handleCloseFullScreenPivotChart = () => {
        window.removeEventListener('popstate', this.handleBackButtonFullScreenPivotChart);

        // Back button was not pushed, reset history item.
        if (location.hash.startsWith(FULL_SCREEN_PIVOT_CHART_HASH_PREFIX)) {
            // Go back to pre-full-screen history state so were not filling it up
            // with blank history items.
            if (!this.state.fullScreenOnMount) history.back();
            // Update url to pre-full-screen state.
            if (
                this.previousHash &&
                !this.previousHash.startsWith(FULL_SCREEN_PIVOT_CHART_HASH_PREFIX)
            ) {
                history.replaceState({}, '', this.previousHash);
            } else {
                history.replaceState({}, '', location.pathname + location.search);
            }
        }

        this.setState({
            fullScreenPivotChart: undefined,
            shouldCloseFullScreenPivotChart: false,
            fullScreenOnMount: false,
        });
    };

    handleBackButtonFullScreenPivotChart = () => {
        this.setState({shouldCloseFullScreenPivotChart: true});
    };

    handleScroll = () => {
        if (!this.containerRef || !this.stickyWrapperRef) return;

        const {isStickyHeaderActive} = this.state;
        const threshold = this.stickyWrapperRef.offsetTop;
        const isThresholdCrossed = this.containerRef.scrollTop > threshold;

        if (isThresholdCrossed && !isStickyHeaderActive) {
            requestAnimationFrame(() => this.setState({isStickyHeaderActive: true}));
        } else if (!isThresholdCrossed && isStickyHeaderActive) {
            requestAnimationFrame(() => this.setState({isStickyHeaderActive: false}));
        }
    };

    handleAnimationEnd = (e: AnimationEvent) => {
        // @ts-expect-error
        if (e.animationName === cardWrapperStyles[POSITION_ANIMATION_NAME]) {
            window.dispatchEvent(new CustomEvent('positionAnimationsFinished'));
        }
    };

    handlePivotFacetChange = (val: ReportFacet) => {
        this.props.onUpdateReportParams({pivotFacet: val});
    };

    handleGapChange = (val: {value: ReportGap}) => {
        this.props.onUpdateReportParams({gap: val.value});
    };

    handleDateRangeTypeChange = (val: {value: DateRangeType}) => {
        this.props.onUpdateReportParams({type: val.value});
    };
}
